import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { X, MessageSquare, Send, Loader2, Plus, Mail, User, CheckCircle2, Sparkles, Check } from 'lucide-react';

interface RequestFeedbackModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface QuestionTemplate {
  id: string;
  role: string;
  category: string;
  question_text: string;
}

interface SelectedQuestion {
  text: string;
  source: 'template' | 'ai' | 'custom';
  id?: string;
}

export default function RequestFeedbackModal({ onClose, onSuccess }: RequestFeedbackModalProps) {
  const { user, profile } = useAuth();
  const { showSuccess, showError } = useToast();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [staticTemplates, setStaticTemplates] = useState<QuestionTemplate[]>([]);
  const [aiSuggestions, setAISuggestions] = useState<string[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<SelectedQuestion[]>([]);
  const [customQuestions, setCustomQuestions] = useState<string[]>([]);

  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAISection, setShowAISection] = useState(false);
  const [goalText, setGoalText] = useState('');

  useEffect(() => {
    if (user && profile) {
      loadRoleBasedTemplates();
    }
  }, [user, profile]);

  const loadRoleBasedTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-question-templates`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ role: profile?.job_title || 'Any' })
        }
      );

      const data = await response.json();
      setStaticTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
      setStaticTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleGenerateAI = async () => {
    setLoadingAI(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        showError('Authentication required');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-question-suggestions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: profile?.job_title || 'Professional',
            goal: goalText || undefined,
            context: {
              job_title: profile?.job_title,
              company: profile?.company
            }
          })
        }
      );

      const data = await response.json();

      if (data.source === 'fallback' || data.source === 'error-fallback') {
        showSuccess('Showing curated questions (AI temporarily unavailable)');
      }

      setAISuggestions(data.suggestions || []);
    } catch (error) {
      showError('Failed to generate suggestions');
      console.error('AI generation error:', error);
    } finally {
      setLoadingAI(false);
    }
  };

  const toggleQuestionSelection = (question: string, source: 'template' | 'ai' | 'custom', id?: string) => {
    const exists = selectedQuestions.find(q => q.text === question);
    if (exists) {
      setSelectedQuestions(selectedQuestions.filter(q => q.text !== question));
    } else {
      if (selectedQuestions.length < 10) {
        setSelectedQuestions([...selectedQuestions, { text: question, source, id }]);
      } else {
        showError('Maximum 10 questions allowed');
      }
    }
  };

  const isQuestionSelected = (question: string) => {
    return selectedQuestions.some(q => q.text === question);
  };

  const addCustomQuestion = () => {
    if (customQuestions.length < 10) {
      setCustomQuestions([...customQuestions, '']);
    }
  };

  const removeCustomQuestion = (index: number) => {
    setCustomQuestions(customQuestions.filter((_, i) => i !== index));
  };

  const updateCustomQuestion = (index: number, value: string) => {
    const updated = [...customQuestions];
    updated[index] = value;
    setCustomQuestions(updated);
  };

  const removeSelectedQuestion = (question: string) => {
    setSelectedQuestions(selectedQuestions.filter(q => q.text !== question));
  };

  const generateUniqueLink = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!recipientEmail.trim()) {
      showError('Please enter an email address');
      return;
    }

    if (!validateEmail(recipientEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    if (!recipientName.trim()) {
      showError('Please enter the recipient\'s name');
      return;
    }

    setLoading(true);

    try {
      const uniqueLink = generateUniqueLink();

      const allQuestions = [
        ...selectedQuestions.map(q => q.text),
        ...customQuestions.filter(q => q.trim().length > 0)
      ];

      const { data: requestData, error: insertError } = await supabase
        .from('feedback_requests')
        .insert({
          requester_id: user.id,
          recipient_email: recipientEmail.trim(),
          recipient_name: recipientName.trim(),
          unique_link: uniqueLink,
          message: message || null,
          custom_questions: allQuestions.length > 0 ? allQuestions : null,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Failed to create feedback request:', insertError);
        showError(insertError.message || 'Failed to create feedback request');
        return;
      }

      if (!requestData) {
        showError('Failed to create request - no data returned');
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      let emailSent = false;
      if (token) {
        const feedbackLink = `${window.location.origin}/feedback/${uniqueLink}`;

        try {
          const emailResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              type: 'feedback_request',
              to: recipientEmail.trim(),
              recipientName: recipientName.trim(),
              requesterName: profile?.full_name || 'A colleague',
              feedbackLink,
              message: message || undefined
            })
          });

          const emailResult = await emailResponse.json();

          if (!emailResponse.ok) {
            console.error('Email sending failed:', emailResult);

            if (emailResult.error?.includes('RESEND_API_KEY') || emailResult.configRequired) {
              showError('Email service not configured. The feedback link has been created, but no email was sent. Please share the link manually.');
            } else {
              showError('Failed to send email notification. The feedback link has been created, but the recipient may not have been notified.');
            }
          } else {
            emailSent = true;
            console.log('Email sent successfully:', emailResult);
          }
        } catch (error) {
          console.error('Failed to send email:', error);
          showError('Network error while sending email. The feedback link has been created but may not have been sent.');
        }
      }

      if (emailSent) {
        setSubmitted(true);
      } else {
        showSuccess('Feedback request created! However, the email may not have been sent. You can share this link manually: ' + `${window.location.origin}/feedback/${uniqueLink}`);
        onSuccess();
      }

    } catch (err: any) {
      console.error('Request submission error:', err);
      showError(err.message || 'Failed to create feedback request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-[24px] max-w-md w-full shadow-2xl p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#1F2937] mb-2">Thank You!</h2>
          <p className="text-[#6B7280] mb-6">
            Your feedback request has been sent successfully. The recipient will receive an email with a link to provide their feedback.
          </p>
          <button
            onClick={onSuccess}
            className="bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[24px] max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-[#1F2937]">Request Feedback</h2>
          <button
            onClick={onClose}
            className="text-[#D1D5DB] hover:text-[#6B7280] transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(90vh-150px)] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-2">
              Recipient Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-[#D1D5DB]" />
              <input
                type="text"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition"
                placeholder="colleague@example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-2">
              Recipient Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 w-5 h-5 text-[#D1D5DB]" />
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition"
                placeholder="John Doe"
                required
              />
            </div>
          </div>

          {staticTemplates.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-[#4B5563] mb-2">
                Suggested Questions for {profile?.job_title || 'You'}
              </label>
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#8CAFA8]" />
                </div>
              ) : (
                <div className="grid gap-2">
                  {staticTemplates.slice(0, 5).map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => toggleQuestionSelection(template.question_text, 'template', template.id)}
                      className={`text-left p-3 rounded-lg border-2 transition ${
                        isQuestionSelected(template.question_text)
                          ? 'border-[#8CAFA8] bg-[#8CAFA8]/5'
                          : 'border-gray-200 hover:border-[#8CAFA8]/50'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                          isQuestionSelected(template.question_text)
                            ? 'border-[#8CAFA8] bg-[#8CAFA8]'
                            : 'border-gray-300'
                        }`}>
                          {isQuestionSelected(template.question_text) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-[#4B5563]">{template.question_text}</p>
                          <p className="text-xs text-[#9CA3AF] mt-1">{template.category}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#4B5563]">
                AI-Powered Suggestions
              </label>
              <button
                type="button"
                onClick={() => setShowAISection(!showAISection)}
                className="text-sm text-[#8CAFA8] hover:text-[#7A9B94] font-medium flex items-center gap-1"
              >
                <Sparkles className="w-4 h-4" />
                {showAISection ? 'Hide' : 'Generate'}
              </button>
            </div>

            {showAISection && (
              <div className="space-y-3 p-4 rounded-lg bg-gradient-to-br from-[#8CAFA8]/5 to-[#8CAFA8]/10 border border-[#8CAFA8]/20">
                <div>
                  <input
                    type="text"
                    value={goalText}
                    onChange={(e) => setGoalText(e.target.value)}
                    placeholder="What do you want feedback on? (optional)"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent text-sm"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleGenerateAI}
                  disabled={loadingAI}
                  className="w-full px-4 py-2 bg-[#8CAFA8] text-white rounded-lg font-medium hover:bg-[#7A9B94] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                >
                  {loadingAI ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate AI Suggestions
                    </>
                  )}
                </button>

                {aiSuggestions.length > 0 && (
                  <div className="grid gap-2 mt-3">
                    {aiSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleQuestionSelection(suggestion, 'ai')}
                        className={`text-left p-3 rounded-lg border-2 transition ${
                          isQuestionSelected(suggestion)
                            ? 'border-[#8CAFA8] bg-white'
                            : 'border-[#8CAFA8]/30 bg-white hover:border-[#8CAFA8]'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                            isQuestionSelected(suggestion)
                              ? 'border-[#8CAFA8] bg-[#8CAFA8]'
                              : 'border-gray-300'
                          }`}>
                            {isQuestionSelected(suggestion) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-[#4B5563]">{suggestion}</p>
                            <p className="text-xs text-[#8CAFA8] mt-1 flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              AI Generated
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedQuestions.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#4B5563]">
                  Selected Questions ({selectedQuestions.length}/10)
                </label>
              </div>
              <div className="space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                {selectedQuestions.map((question, index) => (
                  <div key={index} className="flex items-start gap-2 bg-white p-2 rounded border border-gray-200">
                    <span className="text-[#9CA3AF] font-semibold text-sm mt-1">{index + 1}.</span>
                    <p className="flex-1 text-sm text-[#4B5563] mt-1">{question.text}</p>
                    <button
                      type="button"
                      onClick={() => removeSelectedQuestion(question.text)}
                      className="text-[#D1D5DB] hover:text-red-600 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {customQuestions.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#4B5563]">
                  Custom Questions
                </label>
                <button
                  type="button"
                  onClick={addCustomQuestion}
                  disabled={customQuestions.length >= 10}
                  className="text-sm text-[#8CAFA8] hover:text-[#7A9B94] font-medium disabled:text-[#D1D5DB] disabled:cursor-not-allowed"
                >
                  + Add Question
                </button>
              </div>
              <div className="space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                {customQuestions.map((question, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-[#9CA3AF] font-semibold mt-2 text-sm">{index + 1}.</span>
                    <input
                      type="text"
                      value={question}
                      onChange={(e) => updateCustomQuestion(index, e.target.value)}
                      placeholder="Enter question..."
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomQuestion(index)}
                      className="text-[#D1D5DB] hover:text-red-600 transition mt-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {customQuestions.length === 0 && (
            <button
              type="button"
              onClick={addCustomQuestion}
              className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-[#6B7280] hover:border-[#8CAFA8] hover:text-[#8CAFA8] transition flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Custom Questions
            </button>
          )}

          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-2">
              Personal Message
              <span className="text-[#9CA3AF] font-normal ml-1">(Optional)</span>
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-[#D1D5DB]" />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition resize-none"
                placeholder="I would appreciate your honest feedback on my work and collaboration style..."
                rows={3}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-200 text-[#4B5563] rounded-lg font-semibold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !recipientEmail.trim() || !recipientName.trim()}
              className="flex-1 bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
